'use client';

import * as React from 'react';
import { ChevronRight, ImageIcon, VideoIcon } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { MediaGroupConfig, MediaRecord, PhotoTypeConfig, VideoTypeConfig } from './types';

const VIDEOS_SECTION_CODE = '__videos__';
const VIDEOS_SECTION_LABEL = 'Videos';

interface MediaListColumnProps {
  groups: MediaGroupConfig[];
  photoTypes: PhotoTypeConfig[];
  videoTypes: VideoTypeConfig[];
  photos: MediaRecord[];
  videos: MediaRecord[];
  searchQuery: string;
  statusFilter: 'all' | 'uploaded' | 'pending';
  selectedId: string | null;
  onSelect: (slotCode: string) => void;
  // When set, renders inline directly beneath the currently-selected row
  // instead of relying on a separate detail column/overlay (mobile
  // accordion-style expansion) — see ClaimMediaWorkspace's mobile layout.
  renderInlineDetail?: (slotCode: string) => React.ReactNode;
}

interface SlotRow {
  code: string;
  label: string;
  required: boolean;
  sortOrder: number;
  record: MediaRecord | undefined;
}

interface SlotSection {
  code: string;
  label: string;
  sortOrder: number;
  rows: SlotRow[];
}

function severityLabel(severity: string): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

export function MediaListColumn({
  groups,
  photoTypes,
  videoTypes,
  photos,
  videos,
  searchQuery,
  statusFilter,
  selectedId,
  onSelect,
  renderInlineDetail,
}: MediaListColumnProps) {
  const sections = React.useMemo<SlotSection[]>(() => {
    const photoSections = groups
      .map((group) => {
        const rows = photoTypes
          .filter((type) => type.groupCode === group.code)
          .map((type) => ({
            code: type.code,
            label: type.label,
            required: type.required,
            sortOrder: type.sortOrder,
            record: photos.find((photo) => photo.photo_type === type.code),
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder);

        return { code: group.code, label: group.label, sortOrder: group.sortOrder, rows };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const videoRows = videoTypes
      .map((type) => ({
        code: type.code,
        label: type.label,
        required: false,
        sortOrder: type.sortOrder,
        record: videos.find((video) => video.video_type === type.code),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    return [
      ...photoSections,
      { code: VIDEOS_SECTION_CODE, label: VIDEOS_SECTION_LABEL, sortOrder: Infinity, rows: videoRows },
    ];
  }, [groups, photoTypes, videoTypes, photos, videos]);

  const query = searchQuery.trim().toLowerCase();

  const visibleSections = sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) => {
        if (query && !row.label.toLowerCase().includes(query)) return false;
        if (statusFilter === 'uploaded' && !row.record) return false;
        if (statusFilter === 'pending' && row.record) return false;
        return true;
      }),
    }))
    .filter((section) => section.rows.length > 0);

  return (
    <div className="flex flex-col gap-5">
      {visibleSections.map((section) => (
        <section key={section.code} className="flex flex-col gap-1.5">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {section.label}
          </h2>
          <div className="flex flex-col gap-1.5">
            {section.rows.map((row) => {
              const isSelected = selectedId === row.code;
              const showRequiredMarker = row.required && !row.record;
              const isVideoRow = section.code === VIDEOS_SECTION_CODE;
              const RowIcon = isVideoRow ? VideoIcon : ImageIcon;

              return (
                <React.Fragment key={row.code}>
                  <button
                    type="button"
                    role="button"
                    onClick={() => onSelect(row.code)}
                    aria-current={isSelected ? 'true' : undefined}
                    aria-expanded={renderInlineDetail ? isSelected : undefined}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors',
                      isSelected
                        ? 'border-primary-400 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/30'
                        : 'border-neutral-200 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800'
                    )}
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-neutral-300 bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800">
                      {row.record ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={row.record.download_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <RowIcon className="h-4 w-4 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                      )}
                    </span>

                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="truncate font-medium text-neutral-900 dark:text-neutral-100">
                        {row.label}
                        {showRequiredMarker && <span className="ml-1 text-error-600">*</span>}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {row.record?.detected_damage?.overall_severity && (
                          <Badge variant="warning" size="sm">
                            {severityLabel(row.record.detected_damage.overall_severity)}
                          </Badge>
                        )}
                        <Badge variant={row.record ? 'success' : 'default'} size="sm">
                          {row.record ? 'Saved' : 'Pending'}
                        </Badge>
                      </span>
                    </span>

                    <ChevronRight
                      className={cn(
                        'h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform dark:text-neutral-500',
                        renderInlineDetail && isSelected && 'rotate-90'
                      )}
                      aria-hidden="true"
                    />
                  </button>

                  {renderInlineDetail && isSelected && (
                    <div className="rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900">
                      {renderInlineDetail(row.code)}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
